import { useEffect, useMemo, useState } from 'react'
import { CheckCircle, Clock3, XCircle } from 'lucide-react'
import { adminApi } from '../../services/api'
import type { OrderDetail } from './OrderDetailPanel'
import { useColors } from '../../hooks/useColors'
import { useToast } from '../../contexts/ToastContext'

const PENDING_STATUSES = new Set(['reschedule_pickup_pending', 'reschedule_delivery_pending'])

interface Props {
  order: OrderDetail
  onOrderUpdated: (updated: OrderDetail) => void
}

function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''
  const diffMs = Date.now() - date.getTime()
  const mins = Math.max(0, Math.floor(diffMs / 60000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

export default function ReschedulePendingCard({ order, onOrderUpdated }: Props) {
  const colors = useColors()
  const { addToast } = useToast()
  const [isConfirming, setIsConfirming] = useState(false)
  const [showDeclineBox, setShowDeclineBox] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [isDeclining, setIsDeclining] = useState(false)
  const [capacityState, setCapacityState] = useState<'loading' | 'available' | 'full' | 'unknown'>('loading')

  const isPending = PENDING_STATUSES.has(order.status)
  const isPickup = order.status === 'reschedule_pickup_pending'
  const typeLabel = isPickup ? 'Pickup' : 'Delivery'
  const originalDate = isPickup ? order.pickup_date : order.delivery_date
  const originalSlot = isPickup ? order.pickup_slot : order.delivery_slot
  const requestedDate = isPickup ? order.new_pickup_date : order.new_delivery_date
  const requestedSlot = isPickup ? order.new_pickup_slot : order.new_delivery_slot
  const requestedAt = order.requested_at || order.updated_at
  const requestedAgo = timeAgo(requestedAt)
  const capacityBadge = useMemo(() => {
    if (capacityState === 'available') {
      return (
        <span
          className="font-body"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 999,
            padding: '2px 8px',
            background: 'var(--color-success-bg)',
            color: 'var(--color-success)',
            border: '1px solid var(--color-success-border)',
          }}
        >
          <CheckCircle size={12} /> Slot has capacity
        </span>
      )
    }

    if (capacityState === 'full') {
      return (
        <span
          className="font-body"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 999,
            padding: '2px 8px',
            background: 'var(--color-error-bg)',
            color: 'var(--color-error)',
            border: '1px solid var(--color-error-border)',
          }}
        >
          <XCircle size={12} /> Slot is full
        </span>
      )
    }
    return null
  }, [capacityState])

  useEffect(() => {
    if (!isPending || !requestedDate) {
      setCapacityState('unknown')
      return
    }
    let active = true
    setCapacityState('loading')
    void adminApi
      .getCapacityStatus(requestedDate, requestedSlot || undefined)
      .then((response) => {
        if (!active) return
        const payload = response?.data ?? response
        const remaining = Number(payload?.remaining ?? payload?.available ?? payload?.remaining_orders)
        if (Number.isFinite(remaining)) {
          setCapacityState(remaining > 0 ? 'available' : 'full')
        } else {
          setCapacityState('unknown')
        }
      })
      .catch(() => {
        if (!active) return
        setCapacityState('unknown')
      })
    return () => {
      active = false
    }
  }, [isPending, requestedDate, requestedSlot])

  if (!isPending) return null

  const refreshOrder = async () => {
    const latest = await adminApi.getOrderDetail(order.id)
    onOrderUpdated((latest?.data ?? latest) as OrderDetail)
  }

  const onConfirm = async () => {
    setIsConfirming(true)
    try {
      await adminApi.confirmReschedule(order.id)
      await refreshOrder()
      addToast('Reschedule confirmed, customer notified.', 'success')
    } catch (error: any) {
      addToast(error?.response?.data?.message || 'Unable to confirm reschedule.', 'error')
    } finally {
      setIsConfirming(false)
    }
  }

  const onDecline = async () => {
    const reason = declineReason.trim()
    if (reason.length < 10) return
    setIsDeclining(true)
    try {
      await adminApi.declineReschedule(order.id, reason)
      await refreshOrder()
      addToast('Decline sent to customer.', 'success')
      setShowDeclineBox(false)
      setDeclineReason('')
    } catch (error: any) {
      addToast(error?.response?.data?.message || 'Unable to decline reschedule.', 'error')
    } finally {
      setIsDeclining(false)
    }
  }

  return (
    <div style={{ padding: '0 24px', marginBottom: 12 }}>
      <div
        data-testid="reschedule-pending-card"
        style={{
          marginTop: 12,
          padding: 16,
          borderRadius: 10,
          border: '1px solid var(--color-warning-border)',
          background: 'var(--color-warning-bg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Clock3 size={16} color="var(--color-warning)" />
          <h3 className="font-display" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--color-warning)' }}>
            Reschedule requested
          </h3>
          <span
            className="font-body"
            style={{
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              background: 'var(--color-warning)',
              color: '#fff',
            }}
          >
            {typeLabel}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <p className="font-body" style={{ margin: '0 0 3px', fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', fontWeight: 600 }}>
              Original slot
            </p>
            <p className="font-body" style={{ margin: 0, fontSize: 13, color: colors.textPrimary }}>
              {originalDate || '-'} · {originalSlot || '-'}
            </p>
          </div>
          <div>
            <p className="font-body" style={{ margin: '0 0 3px', fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', fontWeight: 600 }}>
              Requested slot
            </p>
            <p className="font-body" style={{ margin: 0, fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>
              {requestedDate || '-'} · {requestedSlot || '-'}
            </p>
            {capacityBadge ? <div style={{ marginTop: 4 }}>{capacityBadge}</div> : null}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <p className="font-body" style={{ margin: '0 0 3px', fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', fontWeight: 600 }}>
            Customer reason
          </p>
          <p className="font-body" style={{ margin: 0, fontSize: 13, color: colors.textPrimary }}>
            {order.reschedule_reason || '-'}
          </p>
        </div>

        {requestedAgo ? (
          <p className="font-body" style={{ margin: '0 0 14px', fontSize: 12, color: colors.textMuted }}>
            Requested {requestedAgo}
          </p>
        ) : null}

        {!showDeclineBox ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="confirm-reschedule-btn"
              onClick={() => void onConfirm()}
              disabled={isConfirming}
              className="font-body"
              style={{
                border: 'none',
                borderRadius: 6,
                cursor: isConfirming ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 16px',
                opacity: isConfirming ? 0.7 : 1,
                background: 'var(--color-warning)',
                color: '#fff',
              }}
            >
              {isConfirming ? 'Confirming…' : 'Confirm reschedule'}
            </button>
            <button
              type="button"
              data-testid="decline-reschedule-btn"
              onClick={() => setShowDeclineBox(true)}
              className="font-body"
              style={{
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 16px',
                border: '1px solid var(--color-warning)',
                background: 'transparent',
                color: 'var(--color-warning)',
              }}
            >
              Decline &amp; keep original
            </button>
          </div>
        ) : (
          <div>
            <textarea
              data-testid="decline-reason-textarea"
              className="font-body"
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              placeholder="Add decline reason (at least 10 characters)"
              style={{
                width: '100%',
                minHeight: 64,
                resize: 'vertical',
                marginBottom: 8,
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: colors.bgPrimary,
                color: colors.textPrimary,
                fontSize: 13,
                padding: '8px 10px',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                data-testid="decline-confirm-btn"
                onClick={() => void onDecline()}
                disabled={isDeclining || declineReason.trim().length < 10}
                className="font-body"
                style={{
                  border: 'none',
                  borderRadius: 6,
                  cursor: isDeclining || declineReason.trim().length < 10 ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '8px 16px',
                  opacity: isDeclining || declineReason.trim().length < 10 ? 0.6 : 1,
                  background: 'var(--color-error)',
                  color: '#fff',
                }}
              >
                {isDeclining ? 'Declining…' : 'Confirm decline'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeclineBox(false)
                  setDeclineReason('')
                }}
                disabled={isDeclining}
                className="font-body"
                style={{
                  borderRadius: 6,
                  cursor: isDeclining ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '8px 16px',
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.textPrimary,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
